/**
 * Settings Page - Tabbed Interface
 *
 * Allows account owners to configure:
 * - Company Information (name, contact, website, logo, address)
 * - Climate & Location (climate zone with auto-detection)
 * - Billing & Subscription (payment management)
 * - Alerts & Integrations (email, Google Chat)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { State } from 'country-state-city';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { canAccessSettings } from '../utils/permissions';
import { detectClimateFromZip, getClimateZoneOptions, isValidZipCode } from '../utils/climateZones';
import { isOnboardingComplete } from '../utils/onboarding';
import { loadGoogleMapsScript } from '../utils/loadGoogleMaps';
import LogoUpload from '../components/LogoUpload';
import OnboardingBanner from '../components/OnboardingBanner';
import UtilitiesTab from '../components/settings/UtilitiesTab';
import GeographyTab from '../components/settings/GeographyTab';
import RebatesTab from '../components/settings/RebatesTab';
import SystemsTab from '../components/settings/SystemsTab';
import AlertsTab from '../components/settings/AlertsTab';
import AddressAutocomplete from '../components/AddressAutocomplete';
import Toast from '../components/Toast';
import InfoTooltip from '../components/InfoTooltip';

export default function Settings() {
  const { user, contractor, loading: authLoading, refreshContractor } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const showOnboarding = searchParams.get('onboarding') === 'true';

  const [activeTab, setActiveTab] = useState('company');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loadingPortal, setLoadingPortal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');

  // Track if initial load is complete to prevent auto-save on mount
  const initialLoadComplete = useRef(false);
  const saveTimeoutRef = useRef(null);

  // Company Information
  const [companyName, setCompanyName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [companyLogoUrl, setCompanyLogoUrl] = useState('');
  const [financingEnabled, setFinancingEnabled] = useState(false);
  const [financingUrl, setFinancingUrl] = useState('');

  // Climate & Location (also used for business address in Company tab)
  const [officeAddress, setOfficeAddress] = useState('');
  const [officeCity, setOfficeCity] = useState('');
  const [officeState, setOfficeState] = useState('');
  const [officeZip, setOfficeZip] = useState('');
  const [originalState, setOriginalState] = useState(''); // Track original state for change detection
  const [climateZone, setClimateZone] = useState('4A');
  const [coolingHours, setCoolingHours] = useState(1400);
  const [heatingHours, setHeatingHours] = useState(2200);
  const [climateAutoDetected, setClimateAutoDetected] = useState(false);
  const [climateManuallySet, setClimateManuallySet] = useState(false);
  const [detectedZoneInfo, setDetectedZoneInfo] = useState(null);

  // State change confirmation modal
  const [showStateChangeModal, setShowStateChangeModal] = useState(false);
  const [pendingStateChange, setPendingStateChange] = useState(null);

  const tabs = [
    { id: 'company', label: 'Company Info', icon: '🏢' },
    { id: 'geography', label: 'Geography', icon: '🗺️' },
    { id: 'utilities', label: 'Utilities', icon: '⚡' },
    { id: 'climate', label: 'Climate', icon: '🌍' },
    { id: 'systems', label: 'Systems', icon: '🔧' },
    { id: 'rebates', label: 'Rebates', icon: '💰' },
    { id: 'billing', label: 'Billing', icon: '💳' },
    { id: 'alerts', label: 'Alerts', icon: '🔔' }
  ];

  // Handle tab change - clear success message
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSuccess(false);
    setError(null);
  };

  // Permission check
  useEffect(() => {
    if (!authLoading && user && user.role && !canAccessSettings(user)) {
      navigate('/dashboard');
    }
  }, [user, authLoading, navigate]);

  // Load Google Maps API for address autocomplete
  useEffect(() => {
    loadGoogleMapsScript().catch(err => {
      console.error('Failed to load Google Maps:', err);
    });
  }, []);

  // Load contractor data
  useEffect(() => {
    if (contractor) {
      // Company Information
      setCompanyName(contractor.company_name || '');
      setContactEmail(contractor.contact_email || '');
      setCompanyWebsite(contractor.company_website || '');
      setCompanyLogoUrl(contractor.company_logo_url || '');
      setFinancingEnabled(contractor.financing_enabled || false);
      // Sanitize financing URL on load - convert null or invalid values to empty string
      setFinancingUrl(contractor.financing_url && contractor.financing_url.match(/^https?:\/\//) ? contractor.financing_url : '');

      // Climate & Location (also used for business address)
      setOfficeAddress(contractor.office_address || '');
      setOfficeCity(contractor.office_city || '');
      setOfficeState(contractor.office_state || '');
      setOriginalState(contractor.office_state || ''); // Track original state
      setOfficeZip(contractor.office_zip || '');
      setClimateZone(contractor.climate_zone || '4A');
      setCoolingHours(contractor.cooling_hours || 1400);
      setHeatingHours(contractor.heating_hours || 2200);
      setClimateAutoDetected(contractor.climate_auto_detected || false);
      setClimateManuallySet(contractor.climate_manually_set || false);

      setLoading(false);

      // Mark initial load as complete after a brief delay
      setTimeout(() => {
        initialLoadComplete.current = true;
      }, 100);
    }
  }, [contractor]);

  // Auto-save settings with debounce
  const autoSave = useCallback(async () => {
    if (!initialLoadComplete.current || !contractor?.id) return;
    if (showStateChangeModal) return; // Don't auto-save during state change confirmation

    try {
      setSaving(true);

      // Check if onboarding is complete
      const onboardingComplete = isOnboardingComplete({
        company_name: companyName,
        contact_email: contactEmail,
        office_address: officeAddress,
        office_city: officeCity,
        office_state: officeState,
        office_zip: officeZip,
        climate_zone: climateZone
      });

      const updateData = {
        company_name: companyName,
        contact_email: contactEmail,
        company_website: companyWebsite,
        company_logo_url: companyLogoUrl,
        financing_enabled: financingEnabled,
        // Convert empty string to null to satisfy database constraint
        financing_url: financingUrl?.trim() || null,
        office_address: officeAddress,
        office_city: officeCity,
        office_state: officeState,
        office_zip: officeZip,
        climate_zone: climateZone,
        cooling_hours: coolingHours,
        heating_hours: heatingHours,
        climate_auto_detected: climateAutoDetected,
        climate_manually_set: climateManuallySet
      };

      // Mark onboarding as complete if all required fields are filled
      if (onboardingComplete && !contractor.onboarding_completed) {
        updateData.onboarding_completed = true;
        updateData.onboarding_completed_at = new Date().toISOString();
      }

      const { error: updateError } = await supabase
        .from('contractors')
        .update(updateData)
        .eq('id', user.contractor_id);

      if (updateError) throw updateError;

      // Don't refresh contractor data during auto-save to avoid resetting form state
      // Only refresh on critical changes (onboarding completion)
      if (onboardingComplete && !contractor.onboarding_completed) {
        await refreshContractor();
      }

      // Show toast notification (only if not already showing)
      if (!showToast) {
        setToastMessage('Settings saved');
        setToastType('success');
        setShowToast(true);
      }

      // Redirect to dashboard if onboarding was just completed
      if (onboardingComplete && !contractor.onboarding_completed && showOnboarding) {
        setTimeout(() => navigate('/dashboard'), 1500);
      }
    } catch (err) {
      console.error('Error auto-saving settings:', err);

      // Check for financing URL constraint violation
      if (err.code === '23514' && err.message?.includes('financing_url_format')) {
        setToastMessage('Failed to save. Please enter a valid financing URL (starting with http:// or https://)');
      } else {
        setToastMessage('Failed to save settings');
      }

      setToastType('error');
      setShowToast(true);
    } finally {
      setSaving(false);
    }
  }, [
    contractor,
    user,
    companyName,
    contactEmail,
    companyWebsite,
    companyLogoUrl,
    officeAddress,
    officeCity,
    officeState,
    officeZip,
    climateZone,
    coolingHours,
    heatingHours,
    climateAutoDetected,
    climateManuallySet,
    showStateChangeModal,
    showOnboarding,
    navigate,
    refreshContractor
  ]);

  // Debounced auto-save effect - triggers 1200ms after user stops typing
  useEffect(() => {
    if (!initialLoadComplete.current) return;

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set new timeout
    saveTimeoutRef.current = setTimeout(() => {
      autoSave();
    }, 1200); // Increased to 1200ms to give user more time to type

    // Cleanup
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [
    companyName,
    contactEmail,
    companyWebsite,
    companyLogoUrl,
    financingEnabled,
    financingUrl,
    officeAddress,
    officeCity,
    officeState,
    officeZip,
    climateZone,
    coolingHours,
    heatingHours,
    climateManuallySet,
    autoSave
  ]);

  // Handle zip code blur - auto-detect climate zone
  const handleZipCodeBlur = () => {
    if (officeZip && !climateManuallySet && isValidZipCode(officeZip)) {
      const detected = detectClimateFromZip(officeZip);
      if (detected) {
        setClimateZone(detected.zone);
        setCoolingHours(detected.coolingHours);
        setHeatingHours(detected.heatingHours);
        setClimateAutoDetected(true);
        setDetectedZoneInfo(detected);
      }
    }
  };

  // Handle manual climate zone change
  const handleClimateZoneChange = (e) => {
    const zone = e.target.value;
    setClimateZone(zone);
    setClimateManuallySet(true);
    setClimateAutoDetected(false);

    const zoneOptions = getClimateZoneOptions();
    const selectedZone = zoneOptions.find(opt => opt.value === zone);
    if (selectedZone) {
      setCoolingHours(selectedZone.coolingHours);
      setHeatingHours(selectedZone.heatingHours);
    }
  };

  // Handle logo upload success
  const handleLogoUpload = (url) => {
    setCompanyLogoUrl(url);
  };

  // Handle state change with confirmation
  const handleStateChange = (newState) => {
    // If state hasn't changed or no original state yet, allow change
    if (!originalState || newState === originalState || newState === officeState) {
      setOfficeState(newState);
      return;
    }

    // State is changing - show confirmation modal
    setPendingStateChange({
      newState,
      previousAddress: officeAddress,
      previousCity: officeCity,
      previousState: officeState,
      previousZip: officeZip
    });
    setShowStateChangeModal(true);
  };

  // Confirm state change and clear geography
  const confirmStateChange = async () => {
    if (!pendingStateChange) return;

    try {
      // Update state and address
      setOfficeState(pendingStateChange.newState);

      // Auto-detect climate zone if zip code is valid
      if (officeZip && isValidZipCode(officeZip)) {
        const detected = detectClimateFromZip(officeZip);
        if (detected) {
          setClimateZone(detected.zone);
          setCoolingHours(detected.coolingHours);
          setHeatingHours(detected.heatingHours);
          setClimateAutoDetected(true);
          setDetectedZoneInfo(detected);
          setClimateManuallySet(false);
        }
      }

      // Clear all geographic jurisdictions from database
      const { error: deleteError } = await supabase
        .from('geographic_jurisdictions')
        .delete()
        .eq('contractor_id', contractor.id);

      if (deleteError) {
        console.error('Error clearing jurisdictions:', deleteError);
        setError('Failed to clear geographic jurisdictions. Please try again.');
      }

      // Close modal
      setShowStateChangeModal(false);
      setPendingStateChange(null);

      // Show success message
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error confirming state change:', err);
      setError(err.message);
    }
  };

  // Cancel state change and revert
  const cancelStateChange = () => {
    if (pendingStateChange) {
      // Revert to previous values
      setOfficeAddress(pendingStateChange.previousAddress);
      setOfficeCity(pendingStateChange.previousCity);
      setOfficeState(pendingStateChange.previousState);
      setOfficeZip(pendingStateChange.previousZip);
    }
    setShowStateChangeModal(false);
    setPendingStateChange(null);
  };

  // Handle address autocomplete with state change detection
  const handleAddressAutocompleteChange = ({ street, city, state, zip }) => {
    // Check if state is changing
    if (originalState && state && state !== originalState && state !== officeState) {
      // State is changing - show confirmation
      setPendingStateChange({
        newState: state,
        previousAddress: officeAddress,
        previousCity: officeCity,
        previousState: officeState,
        previousZip: officeZip
      });
      setShowStateChangeModal(true);

      // Temporarily set new values (will be reverted if cancelled)
      if (street) setOfficeAddress(street);
      if (city) setOfficeCity(city);
      if (state) setOfficeState(state);
      if (zip) setOfficeZip(zip);
    } else {
      // No state change - update normally
      if (street) setOfficeAddress(street);
      if (city) setOfficeCity(city);
      if (state) setOfficeState(state);
      if (zip) setOfficeZip(zip);

      // Auto-detect climate zone if zip changed
      if (zip && isValidZipCode(zip)) {
        const detected = detectClimateFromZip(zip);
        if (detected) {
          setClimateZone(detected.zone);
          setCoolingHours(detected.coolingHours);
          setHeatingHours(detected.heatingHours);
          setClimateAutoDetected(true);
          setDetectedZoneInfo(detected);
          setClimateManuallySet(false);
        }
      }
    }
  };


  // Handle billing portal
  const handleManageBilling = async () => {
    if (!contractor?.stripe_customer_id) {
      setError('No billing information found. Please complete payment setup first.');
      return;
    }

    setLoadingPortal(true);
    setError(null);

    try {
      const response = await fetch('/.netlify/functions/create-portal-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId: contractor.stripe_customer_id })
      });

      const data = await response.json();

      if (data.error || !data.url) {
        throw new Error(data.error || data.message || 'Failed to open billing portal');
      }

      window.location.href = data.url;
    } catch (err) {
      console.error('Error opening billing portal:', err);
      setError(err.message || 'Failed to open billing portal. Please try again.');
      setLoadingPortal(false);
    }
  };

  if (loading) {
    return (
      <div className="container" style={{ paddingTop: '40px' }}>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: '18px', color: '#64748b' }}>Loading settings...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingTop: '40px' }}>
      {/* Header */}
      <div style={{ marginBottom: '30px', maxWidth: '1200px', margin: '0 auto 30px auto' }}>
        <h1 style={{ fontSize: '28px', marginBottom: '5px' }}>Settings</h1>
        <p style={{ color: '#7f8c8d', margin: 0 }}>
          Configure your company information and preferences
        </p>
      </div>

      {/* Onboarding Banner - Removed in favor of Welcome Modal */}

      {/* Success Message */}
      {success && (
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto 20px auto',
          padding: '15px',
          background: '#dcfce7',
          border: '2px solid #22c55e',
          borderRadius: '8px',
          color: '#14532d',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <span>✅</span>
          <strong>Settings saved successfully!</strong>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto 20px auto',
          padding: '15px',
          background: '#fee2e2',
          border: '2px solid #ef4444',
          borderRadius: '8px',
          color: '#dc2626'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* State Change Confirmation Modal */}
      {showStateChangeModal && pendingStateChange && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            maxWidth: '600px',
            width: '100%',
            padding: '30px',
            boxShadow: '0 20px 50px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{ fontSize: '24px', fontWeight: '700', marginBottom: '15px', color: '#dc2626' }}>
              ⚠️ State Change Warning
            </div>

            <div style={{ fontSize: '16px', color: '#374151', marginBottom: '20px', lineHeight: '1.6' }}>
              <p style={{ marginBottom: '12px' }}>
                You are about to change your company's registered state from{' '}
                <strong>{State.getStatesOfCountry('US').find(s => s.isoCode === pendingStateChange.previousState)?.name || pendingStateChange.previousState}</strong>{' '}
                to{' '}
                <strong>{State.getStatesOfCountry('US').find(s => s.isoCode === pendingStateChange.newState)?.name || pendingStateChange.newState}</strong>.
              </p>

              <div style={{
                padding: '15px',
                background: '#fef3c7',
                borderRadius: '8px',
                marginBottom: '15px'
              }}>
                <strong style={{ color: '#92400e' }}>This action will:</strong>
                <ul style={{ marginTop: '8px', marginBottom: 0, paddingLeft: '20px', color: '#78350f' }}>
                  <li>Clear ALL selected counties and cities from Geography settings</li>
                  <li>Reset your climate zone based on the new address</li>
                  <li>Affect your license coverage and service areas</li>
                </ul>
              </div>

              <p style={{ margin: 0, fontWeight: '600', color: '#1f2937' }}>
                Are you sure you want to continue?
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={cancelStateChange}
                style={{
                  padding: '12px 24px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  background: 'white',
                  color: '#374151',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = '#f9fafb'}
                onMouseLeave={(e) => e.target.style.background = 'white'}
              >
                ← No, Keep Current State
              </button>
              <button
                type="button"
                onClick={confirmStateChange}
                style={{
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
                  color: 'white',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
                onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
              >
                Yes, Change State →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Layout with Left Sidebar Navigation */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: '250px 1fr',
        gap: '30px',
        alignItems: 'start'
      }}>
        {/* Left Sidebar Navigation */}
        <div style={{
          position: 'sticky',
          top: '20px'
        }}>
          <nav style={{
            background: 'white',
            borderRadius: '12px',
            padding: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            {tabs.map(tab => (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleTabChange(tab.id)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  marginBottom: '4px',
                  border: 'none',
                  borderRadius: '8px',
                  background: activeTab === tab.id ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                  color: activeTab === tab.id ? 'white' : '#475569',
                  fontSize: '15px',
                  fontWeight: activeTab === tab.id ? '600' : '500',
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== tab.id) {
                    e.target.style.background = '#f8fafc';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== tab.id) {
                    e.target.style.background = 'transparent';
                  }
                }}
              >
                <span style={{ fontSize: '20px' }}>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Right Content Area */}
        <div>

          {/* Company Information Tab */}
          {activeTab === 'company' && (
            <div className="card">
              <h2 style={{ fontSize: '20px', marginBottom: '20px' }}>Company Information</h2>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Company Name <span style={{ color: '#ef4444' }}>*</span>
                  <InfoTooltip text="This is the customer-facing company name that will display on assessment reports sent to your customers." />
                </label>
                <input
                  type="text"
                  className="input"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Your HVAC Company LLC"
                  required
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Contact Email <span style={{ color: '#ef4444' }}>*</span>
                  <InfoTooltip text="This is the contact email that will allow customers to contact you from the reports you share with them." />
                </label>
                <input
                  type="email"
                  className="input"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="contact@yourhvac.com"
                  required
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Company Website
                  <InfoTooltip text="This is the website or landing page you will send customers to if they click for more information from a report sent to them." />
                </label>
                <input
                  type="url"
                  className="input"
                  value={companyWebsite}
                  onChange={(e) => setCompanyWebsite(e.target.value)}
                  placeholder="https://yourhvac.com"
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <LogoUpload
                  currentLogoUrl={companyLogoUrl}
                  contractorId={contractor.id}
                  onUploadSuccess={handleLogoUpload}
                />
              </div>

              {/* Financing Options */}
              <div style={{ marginBottom: '20px', marginTop: '30px' }}>
                <h3 style={{ fontSize: '18px', marginBottom: '15px', display: 'flex', alignItems: 'center' }}>
                  Financing Options
                  <InfoTooltip text="Configure financing application link that appears on all system options in customer assessments" />
                </h3>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}>
                    <input
                      type="checkbox"
                      checked={financingEnabled}
                      onChange={(e) => setFinancingEnabled(e.target.checked)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    Enable "Apply for Financing" button on system options
                  </label>
                  <div style={{ fontSize: '13px', color: '#64748b', marginTop: '6px', marginLeft: '28px' }}>
                    When enabled, a financing button will appear on all system options in assessment results
                  </div>
                </div>

                {financingEnabled && (
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                      Financing Application URL <span style={{ color: '#ef4444' }}>*</span>
                      <InfoTooltip text="Customers will be directed to this URL to apply for financing. Must be a valid https:// or http:// link." />
                    </label>
                    <input
                      type="url"
                      className="input"
                      value={financingUrl}
                      onChange={(e) => setFinancingUrl(e.target.value)}
                      placeholder="https://apply.yourfinancingpartner.com"
                      required={financingEnabled}
                      style={{
                        borderColor: financingEnabled && financingUrl && !financingUrl.match(/^https?:\/\//)
                          ? '#f97316'
                          : undefined
                      }}
                    />
                    {financingEnabled && financingUrl && !financingUrl.match(/^https?:\/\//) && (
                      <div style={{ fontSize: '12px', color: '#f97316', marginTop: '6px', fontWeight: '600' }}>
                        ⚠️ URL must start with http:// or https://
                      </div>
                    )}
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '6px' }}>
                      Example: https://apply.wisetack.com/your-company
                    </div>
                  </div>
                )}
              </div>

              <h3 style={{ fontSize: '18px', marginBottom: '15px', marginTop: '30px', display: 'flex', alignItems: 'center' }}>
                Business Address
                <InfoTooltip text="This business address will be displayed on assessment reports sent to your customers." />
              </h3>

              <div style={{
                padding: '12px',
                background: '#fffbeb',
                border: '2px solid #fbbf24',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#92400e',
                lineHeight: '1.6',
                marginBottom: '20px'
              }}>
                <strong>⚠️ Important:</strong> Your state determines your license coverage and geographic service areas. Changing this will clear selections previously made in the Geography section and will also change the Climate Zone settings.
              </div>

              <div style={{ marginBottom: '20px' }}>
                <AddressAutocomplete
                  street={officeAddress}
                  city={officeCity}
                  state={officeState}
                  zip={officeZip}
                  onStreetChange={setOfficeAddress}
                  onCityChange={setOfficeCity}
                  onStateChange={setOfficeState}
                  onZipChange={setOfficeZip}
                  onAddressSelect={handleAddressAutocompleteChange}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    City <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    className="input"
                    value={officeCity}
                    onChange={(e) => setOfficeCity(e.target.value)}
                    placeholder="Greensboro"
                    required
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    State <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <select
                    className="input"
                    value={officeState}
                    onChange={(e) => handleStateChange(e.target.value)}
                    required
                    style={{ width: '100%' }}
                  >
                    <option value="">Select State</option>
                    {State.getStatesOfCountry('US').map(state => (
                      <option key={state.isoCode} value={state.isoCode}>
                        {state.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    Zip <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    className="input"
                    value={officeZip}
                    onChange={(e) => setOfficeZip(e.target.value)}
                    onBlur={handleZipCodeBlur}
                    placeholder="27401"
                    maxLength={5}
                    required
                  />
                </div>
              </div>
            </div>
          )}

          {/* Geography Tab */}
          {activeTab === 'geography' && <GeographyTab />}

          {/* Utilities Tab */}
          {activeTab === 'utilities' && <UtilitiesTab />}

          {/* Climate & Location Tab */}
          {activeTab === 'climate' && (
            <div className="card">
              <h2 style={{ fontSize: '20px', marginBottom: '20px' }}>Climate & Location</h2>

              <div style={{
                padding: '15px',
                background: '#f0f9ff',
                borderRadius: '8px',
                marginBottom: '20px',
                fontSize: '14px',
                color: '#0c4a6e'
              }}>
                <strong>Note:</strong> Your office address is used to automatically detect your climate zone.
                Update your address in the Company Info tab.
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Current Address
                </label>
                <div style={{
                  padding: '12px',
                  background: '#f8fafc',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: '#64748b'
                }}>
                  {officeAddress || 'No address set'}<br />
                  {officeCity && `${officeCity}, ${officeState} ${officeZip}`}
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Climate Zone <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <select
                  className="input"
                  value={climateZone}
                  onChange={handleClimateZoneChange}
                  style={{ width: '100%' }}
                  required
                >
                  {getClimateZoneOptions().map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Auto-detection indicator */}
              {climateAutoDetected && detectedZoneInfo && (
                <div style={{
                  padding: '12px',
                  background: '#dcfce7',
                  border: '2px solid #22c55e',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: '#14532d',
                  marginBottom: '15px'
                }}>
                  <strong>✓ Auto-detected</strong> from zip code {officeZip}
                </div>
              )}

              {/* Climate hours info */}
              <div style={{
                padding: '15px',
                background: '#f0f9ff',
                borderRadius: '6px',
                fontSize: '14px',
                color: '#0c4a6e',
                marginBottom: '15px'
              }}>
                <div style={{ fontWeight: '600', marginBottom: '8px' }}>
                  Climate Data for Selected Zone:
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                  <div>
                    <div style={{ color: '#64748b', fontSize: '12px' }}>Cooling Hours/Year</div>
                    <div style={{ fontWeight: '600', fontSize: '16px' }}>{coolingHours.toLocaleString()}</div>
                  </div>
                  <div>
                    <div style={{ color: '#64748b', fontSize: '12px' }}>Heating Hours/Year</div>
                    <div style={{ fontWeight: '600', fontSize: '16px' }}>{heatingHours.toLocaleString()}</div>
                  </div>
                </div>
                <div style={{ fontSize: '11px', color: '#64748b', fontStyle: 'italic', borderTop: '1px solid #bae6fd', paddingTop: '8px' }}>
                  Estimated annual hours based on IECC 2021 climate zone classifications and ASHRAE Standard 169-2021 climatic data for building design standards.
                </div>
              </div>

              {/* Impact message */}
              <div style={{
                padding: '12px',
                background: '#fffbeb',
                borderRadius: '6px',
                fontSize: '13px',
                color: '#92400e',
                lineHeight: '1.5'
              }}>
                <strong>Important:</strong> Your climate zone affects HVAC calculations for energy costs and system recommendations. Make sure this is accurate for your service area.
              </div>
            </div>
          )}

          {/* Systems Tab */}
          {activeTab === 'systems' && <SystemsTab />}

          {/* Rebates Tab */}
          {activeTab === 'rebates' && <RebatesTab />}

          {/* Billing & Subscription Tab */}
          {activeTab === 'billing' && (
            <div className="card">
              <h2 style={{ fontSize: '20px', marginBottom: '20px' }}>Billing & Subscription</h2>

              <div style={{ display: 'grid', gap: '15px', marginBottom: '20px' }}>
                <div>
                  <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '4px' }}>
                    Status
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#2c3e50', textTransform: 'capitalize' }}>
                      {contractor?.subscription_status || 'Active'}
                    </div>
                    {contractor?.trial_ends_at && new Date(contractor.trial_ends_at) > new Date() && (
                      <span style={{
                        padding: '4px 8px',
                        background: '#fef3c7',
                        color: '#92400e',
                        fontSize: '12px',
                        fontWeight: '600',
                        borderRadius: '4px'
                      }}>
                        Trial: {Math.ceil((new Date(contractor.trial_ends_at) - new Date()) / (1000 * 60 * 60 * 24))} days left
                      </span>
                    )}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '4px' }}>
                    Assessments This Period
                  </div>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#2c3e50' }}>
                    {contractor?.assessments_this_period || 0} / {contractor?.included_assessments || 500}
                  </div>
                </div>

                <div style={{
                  padding: '12px',
                  background: '#f0f9ff',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: '#0c4a6e'
                }}>
                  <strong>Plan:</strong> $1,299/month with 500 included assessments
                  <br />
                  <strong>Overage:</strong> $0.39 per additional assessment
                </div>
              </div>

              {!contractor?.stripe_customer_id ? (
                <div>
                  <div style={{
                    padding: '16px',
                    background: '#fef3c7',
                    border: '2px solid #f59e0b',
                    borderRadius: '8px',
                    marginBottom: '12px'
                  }}>
                    <div style={{ fontWeight: '600', color: '#92400e', marginBottom: '8px' }}>
                      ⚠️ Payment Setup Required
                    </div>
                    <div style={{ fontSize: '14px', color: '#92400e', marginBottom: '12px' }}>
                      Complete payment setup to activate your subscription and access billing management.
                    </div>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          setError(null);
                          const response = await fetch('/.netlify/functions/create-checkout-session', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              email: user.email,
                              contractorId: contractor.id
                            })
                          });

                          const data = await response.json();

                          if (!response.ok) {
                            throw new Error(data.message || data.error || `Server error: ${response.status}`);
                          }

                          if (data.url) {
                            window.location.href = data.url;
                          } else {
                            throw new Error('No checkout URL received');
                          }
                        } catch (err) {
                          console.error('Error creating checkout:', err);
                          setError(`Failed to create checkout: ${err.message}`);
                        }
                      }}
                      className="btn btn-primary"
                      style={{ width: '100%' }}
                    >
                      Complete Payment Setup →
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleManageBilling}
                  className="btn btn-secondary"
                  disabled={loadingPortal}
                  style={{ width: '100%' }}
                >
                  {loadingPortal ? 'Opening Billing Portal...' : '💳 Manage Billing & Payment Methods'}
                </button>
              )}
            </div>
          )}

          {/* Alerts & Integrations Tab */}
          {activeTab === 'alerts' && <AlertsTab />}

        </div>
      </div>

      {/* Toast Notification */}
      {showToast && (
        <Toast
          message={toastMessage}
          type={toastType}
          onClose={() => setShowToast(false)}
        />
      )}
    </div>
  );
}
